/**
 * Left column of the Inbox: search, filters (streams, dates, categories, signals), tabs, and chat list.
 *
 * Where: `Inbox` page (`view === 'list'` or desktop master pane).
 * Uses: `chatApi`, `EntityMultiSelect`, date pickers, auth; drives selected chat id for parent.
 * Behavior: infinite scroll, filter state sync, read/archive mutations, responsive collapse.
 */
import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  Autocomplete,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import { EntityMultiSelect } from '@/components/common/EntityMultiSelect';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarMonth as CalendarIcon,
  EventBusy as EventBusyIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import type { Chat, MessageCategory, ProjectInfo, StreamInfo } from '@/types/chat';
import { getCategoryLabel, getCategoryColor, CATEGORY_FILTER_OPTIONS } from '@/constants/conversationCategoryCatalog';
import { chatApi } from '@/api/chat';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { GlobalMessageResyncButton } from './GlobalMessageResyncButton';

interface FilterState {
  search: string;
  readStatus: 'all' | 'messages' | 'unread' | 'read' | 'archived' | 'important' | 'last_receiver';
  streams: string[];
  dateFrom: Date | null;
  dateTo: Date | null;
  messageCategories: string[];
  conversationSignals: string[];
  conversationState: string | null;
  owners: string[];
  tags: string[];
  lastSenders: string[];
}

interface MessageListProps {
  filters: FilterState;
  onFilterChange: (key: keyof FilterState, value: any) => void;
  selectedChat: Chat | null;
  onChatSelect: (chat: Chat) => void;
  chats: Chat[];
  isLoading: boolean;
  error: any;
  streams: any[];
  owners: { uuid: string; name: string }[];
  messageCategories: any[];
  conversationSignalOptions: {
    value: string;
    label: string;
    description: string;
    dot: string;
    bg: string;
    text: string;
    border: string;
  }[];
  expandedFilters: boolean;
  onExpandedFiltersChange: (expanded: boolean) => void;
  activeTab: number;
  onTabChange: (_: React.SyntheticEvent, newValue: number) => void;
  // Infinite scroll
  fetchNextPage: () => void | Promise<any>;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────

// Tag component for displaying project/stream and message categories
const Tag: React.FC<{
  label: string;
  color?: string;
  variant?: 'outlined' | 'filled';
  size?: 'small' | 'medium';
  sx?: any;
}> = ({ label, color, variant = 'outlined', size = 'small', sx }) => (
  <Chip
    label={label}
    size={size}
    variant={variant}
    sx={{
      backgroundColor: variant === 'filled' ? color : 'transparent',
      color: variant === 'filled' ? 'white' : color,
      borderColor: color,
      border: variant === 'outlined' ? `1px solid ${color}` : 'none',
      fontSize: '0.7rem',
      height: '20px',
      '& .MuiChip-label': {
        px: 1,
      },
      ...sx,
    }}
  />
);

// Project/Stream combined tag
const ProjectStreamTag: React.FC<{
  project?: ProjectInfo | null;
  stream?: StreamInfo | null;
  compact?: boolean;
}> = ({ project, stream, compact = false }) => {
  if (!project && !stream) return null;
  
  const label = project && stream 
    ? `${project.name} / ${stream.name}`
    : (project?.name || stream?.name || '');
  
  const truncateLabel = (label: string) => {
    if (!compact) return label;
    if (label.length > 20) {
      return label.substring(0, 18) + '...';
    }
    return label;
  };
  
  return (
    <Tooltip 
      title={label}
      placement="top"
      arrow
    >
      <Box>
        <Tag
          label={truncateLabel(label)}
          color="#1976d2"
          variant="outlined"
          sx={compact ? {
            fontSize: '0.6rem',
            height: '16px',
            '& .MuiChip-label': {
              px: 0.5,
            },
          } : undefined}
        />
      </Box>
    </Tooltip>
  );
};

// Tags component
const TagsDisplay: React.FC<{
  tags: string[];
  compact?: boolean;
}> = ({ tags, compact = false }) => {
  if (!tags || tags.length === 0) return null;
  
  const displayTags = compact ? tags.slice(0, 2) : tags;
  
  const truncateLabel = (label: string) => {
    if (!compact) return label;
    if (label.length > 12) {
      return label.substring(0, 10) + '...';
    }
    return label;
  };
  
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {displayTags.map((tag) => (
        <Tooltip 
          key={tag}
          title={tag}
          placement="top"
          arrow
        >
          <Box>
            <Tag
              label={truncateLabel(tag)}
              color="#9C27B0"
              variant="outlined"
              size={compact ? 'small' : 'small'}
              sx={compact ? {
                fontSize: '0.6rem',
                height: '16px',
                '& .MuiChip-label': {
                  px: 0.5,
                },
              } : undefined}
            />
          </Box>
        </Tooltip>
      ))}
      {compact && tags.length > 2 && (
        <Tooltip 
          title={`${tags.slice(2).join(', ')}`}
          placement="top"
          arrow
        >
          <Box>
            <Chip
              label={`+${tags.length - 2} more`}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.6rem',
                height: '16px',
                color: 'text.secondary',
                borderColor: 'text.secondary',
                '& .MuiChip-label': {
                  px: 0.5,
                },
              }}
            />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

// Message categories tags
const MessageCategoriesTags: React.FC<{
  categories: MessageCategory[];
  compact?: boolean;
}> = ({ categories, compact = false }) => {
  if (!categories || categories.length === 0) return null;
  
  // For compact mode, limit to 2 categories and truncate long names
  const displayCategories = compact 
    ? categories.slice(0, 2)
    : categories;
  
  const truncateLabel = (label: string) => {
    if (!compact) return label;
    if (label.length > 12) {
      return label.substring(0, 10) + '...';
    }
    return label;
  };
  
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {displayCategories.map((category) => (
        <Tooltip 
          key={category.key}
          title={category.label}
          placement="top"
          arrow
        >
          <Box>
            <Tag
              label={truncateLabel(category.label)}
              color={category.color}
              variant="filled"
              size={compact ? 'small' : 'small'}
              sx={compact ? {
                fontSize: '0.6rem',
                height: '16px',
                '& .MuiChip-label': {
                  px: 0.5,
                },
              } : undefined}
            />
          </Box>
        </Tooltip>
      ))}
      {compact && categories.length > 2 && (
        <Tooltip 
          title={`${categories.slice(2).map(cat => cat.label).join(', ')}`}
          placement="top"
          arrow
        >
          <Box>
            <Chip
              label={`+${categories.length - 2} more`}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.6rem',
                height: '16px',
                color: 'text.secondary',
                borderColor: 'text.secondary',
                '& .MuiChip-label': {
                  px: 0.5,
                },
              }}
            />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

const EmptyState: React.FC<{ 
  title: string; 
  description: string; 
  icon?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ title, description, icon, action }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      textAlign: 'center',
      p: 4,
    }}
  >
    {icon && (
      <Box sx={{ mb: 2, color: 'text.secondary' }}>
        {icon}
      </Box>
    )}
    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {description}
    </Typography>
    {action}
  </Box>
);

export const MessageList: React.FC<MessageListProps> = ({
  filters,
  onFilterChange,
  selectedChat,
  onChatSelect,
  chats,
  isLoading,
  error,
  streams,
  owners,
  messageCategories,
  conversationSignalOptions,
  expandedFilters,
  onExpandedFiltersChange,
  activeTab,
  onTabChange,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const isAdmin = user?.is_staff || user?.is_superuser;
  const queryClient = useQueryClient();

  // Fetch all tags
  const { data: allTags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: chatApi.getTags,
  });

  // Create tag mutation (admin only)
  const createTagMutation = useMutation({
    mutationFn: (title: string) => chatApi.createTag(title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });

  // Count active filters to drive badge and button styling
  const activeFilterCount = [
    filters.streams.length > 0,
    filters.owners.length > 0,
    filters.messageCategories.length > 0,
    filters.conversationSignals.length > 0,
    filters.conversationState !== null,
    filters.tags.length > 0,
    filters.lastSenders.length > 0,
    filters.dateFrom !== null,
    filters.dateTo !== null,
  ].filter(Boolean).length;

  // Use chats directly from API response since filtering is now handled on backend
  const filteredChats = chats;



  // Refs for intersection observer
  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const sentinel = loadMoreRef.current;
    if (!hasNextPage || !container || !sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: container,
        rootMargin: '200px',
      },
    );
    observer.observe(sentinel);
    return () => {
      observer.unobserve(sentinel);
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  return (
    <Box sx={{ width: '100%', height: '100vh', maxHeight: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', py: 2}}>
      {/* Header - Only show on desktop/tablet */}
      {!isMobile && (
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
              Inbox
            </Typography>
            <GlobalMessageResyncButton />
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search"
            value={filters.search}
            onChange={(e) => onFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />

          {/* Tabs */}
          <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
            <Tabs
              value={activeTab}
              onChange={onTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 32,
                '& .MuiTab-root': {
                  minHeight: 32,
                  fontSize: '0.8rem',
                  py: 0,
                  minWidth: 'auto',
                  px: 1,
                },
              }}
            >
              <Tab label="All" />
              <Tab label="Messages" />
              <Tab label="Awaiting Response" />
              <Tab label="Unread" />
              <Tab label="Read" />
              <Tab label="Important" />
              <Tab label="Archived" />
            </Tabs>
          </Box>
        </Box>
      )}

      {/* For mobile, render Search and Tabs directly below, without Inbox header */}
      {isMobile && (
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search"
            value={filters.search}
            onChange={(e) => onFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
          <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
            <Tabs
              value={activeTab}
              onChange={onTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 32,
                '& .MuiTab-root': {
                  minHeight: 32,
                  fontSize: '0.8rem',
                  py: 0,
                  minWidth: 'auto',
                  px: 1,
                },
              }}
            >
              <Tab label="All" />
              <Tab label="Messages" />
              <Tab label="Awaiting Response" />
              <Tab label="Unread" />
              <Tab label="Read" />
              <Tab label="Important" />
              <Tab label="Archived" />
            </Tabs>
          </Box>
        </Box>
      )}

      {/* Filters */}
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        {/* Filter toggle button with active-count badge */}
        <Button
          fullWidth
          variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
          color={activeFilterCount > 0 ? 'primary' : 'inherit'}
          size="small"
          onClick={() => onExpandedFiltersChange(!expandedFilters)}
          endIcon={expandedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          startIcon={
            <Badge badgeContent={activeFilterCount} color="error" invisible={activeFilterCount === 0}>
              <FilterListIcon />
            </Badge>
          }
          sx={{ mb: (activeFilterCount > 0 || expandedFilters) ? 1 : 0 }}
        >
          {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filters'}
        </Button>

        {/* Active filter chips — always visible when any filter is set */}
        {activeFilterCount > 0 && !expandedFilters && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {/* Stream chips */}
            {filters.streams.map((uuid) => {
              const stream = streams.find((s: any) => s.uuid === uuid);
              return stream ? (
                <Chip
                  key={uuid}
                  label={stream.name}
                  size="small"
                  onDelete={() => onFilterChange('streams', filters.streams.filter((s) => s !== uuid))}
                  sx={{ fontSize: '0.7rem', height: '22px' }}
                />
              ) : null;
            })}
            {/* Owner chips */}
            {filters.owners.map((uuid) => {
              const owner = owners.find((o) => o.uuid === uuid);
              return owner ? (
                <Chip
                  key={uuid}
                  label={owner.name}
                  size="small"
                  onDelete={() => onFilterChange('owners', filters.owners.filter((o) => o !== uuid))}
                  sx={{ fontSize: '0.7rem', height: '22px' }}
                />
              ) : null;
            })}
            {/* Message category chips */}
            {filters.messageCategories.map((key) => (
              <Chip
                key={key}
                label={key}
                size="small"
                onDelete={() => onFilterChange('messageCategories', filters.messageCategories.filter((c) => c !== key))}
                sx={{ fontSize: '0.7rem', height: '22px' }}
              />
            ))}
            {/* Conversation signal chips */}
            {filters.conversationSignals.map((sig) => {
              const opt = conversationSignalOptions.find((o) => o.value === sig);
              return (
                <Chip
                  key={sig}
                  label={opt?.label ?? sig}
                  size="small"
                  onDelete={() => onFilterChange('conversationSignals', filters.conversationSignals.filter((s) => s !== sig))}
                  sx={{
                    fontSize: '0.7rem',
                    height: '22px',
                    bgcolor: opt?.bg,
                    color: opt?.text,
                    border: `1px solid ${opt?.border}`,
                    '& .MuiChip-deleteIcon': { color: opt?.text, opacity: 0.7 },
                  }}
                />
              );
            })}
            {/* Conversation state chip */}
            {filters.conversationState && (
              <Chip
                label={getCategoryLabel(filters.conversationState)}
                size="small"
                onDelete={() => onFilterChange('conversationState', null)}
                sx={{
                  fontSize: '0.7rem',
                  height: '22px',
                  bgcolor: getCategoryColor(filters.conversationState),
                  color: '#fff',
                  '& .MuiChip-deleteIcon': { color: '#fff', opacity: 0.7 },
                }}
              />
            )}
            {/* Tag chips */}
            {filters.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onDelete={() => onFilterChange('tags', filters.tags.filter((t) => t !== tag))}
                sx={{ fontSize: '0.7rem', height: '22px' }}
              />
            ))}
            {/* Last sender chips */}
            {filters.lastSenders.map((s) => (
              <Chip
                key={s}
                label={s === 'pipeline' ? 'Pipeline' : s === 'system' ? 'System' : (owners.find((o) => o.uuid === s)?.name ?? s)}
                size="small"
                onDelete={() => onFilterChange('lastSenders', filters.lastSenders.filter((x) => x !== s))}
                sx={{ fontSize: '0.7rem', height: '22px' }}
              />
            ))}
            {/* Date range chip */}
            {(filters.dateFrom || filters.dateTo) && (
              <Chip
                label={`${filters.dateFrom ? new Date(filters.dateFrom).toLocaleDateString() : '…'} – ${filters.dateTo ? new Date(filters.dateTo).toLocaleDateString() : '…'}`}
                size="small"
                onDelete={() => { onFilterChange('dateFrom', null); onFilterChange('dateTo', null); }}
                sx={{ fontSize: '0.7rem', height: '22px' }}
              />
            )}
            {/* Clear all */}
            <Chip
              label="Clear all"
              size="small"
              color="error"
              variant="outlined"
              onClick={() => {
                onFilterChange('streams', []);
                onFilterChange('owners', []);
                onFilterChange('messageCategories', []);
                onFilterChange('conversationSignals', []);
                onFilterChange('conversationState', null);
                onFilterChange('tags', []);
                onFilterChange('lastSenders', []);
                onFilterChange('dateFrom', null);
                onFilterChange('dateTo', null);
              }}
              sx={{ fontSize: '0.7rem', height: '22px' }}
            />
          </Box>
        )}
        
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Collapse in={expandedFilters}>
            <Stack spacing={1}>
              {/* Stream Filter */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Experiment
                </Typography>
                <EntityMultiSelect
                  options={streams}
                  valueUuids={filters.streams}
                  onChange={(uuids) => onFilterChange('streams', uuids)}
                  placeholder="Select experiments"
                  size="small"
                />
              </Box>

              {/* Owner Filter */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Operator
                </Typography>
                <EntityMultiSelect
                  options={owners}
                  valueUuids={filters.owners}
                  onChange={(uuids) => onFilterChange('owners', uuids)}
                  placeholder="Select operators"
                  size="small"
                />
              </Box>

              {/* Date Range Filter */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Date Range
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <DatePicker
                    label="From"
                    value={filters.dateFrom}
                    onChange={(date) => onFilterChange('dateFrom', date)}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <DatePicker
                    label="To"
                    value={filters.dateTo}
                    onChange={(date) => onFilterChange('dateTo', date)}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </Box>
              </Box>

              {/* Message Categories Filter */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Message Categories
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={messageCategories}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.key === value.key}
                  value={messageCategories.filter(cat => filters.messageCategories.includes(cat.key))}
                  onChange={(_, value) => {
                    const keys = value.map(v => v.key);
                    onFilterChange('messageCategories', keys);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select categories" size="small" />
                  )}
                />
              </Box>

              {/* Conversation Signal Filter */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  AI Signal
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={conversationSignalOptions}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  value={conversationSignalOptions.filter((o) => filters.conversationSignals.includes(o.value))}
                  onChange={(_, selected) => {
                    onFilterChange('conversationSignals', selected.map((s) => s.value));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Filter by signal" size="small" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        label={option.label}
                        size="small"
                        sx={{
                          bgcolor: option.bg,
                          color: option.text,
                          border: `1px solid ${option.border}`,
                          fontSize: '0.7rem',
                          height: '20px',
                        }}
                      />
                    ))
                  }
                  renderOption={({ key, ...props }, option) => (
                    <li key={key} {...props} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 12px' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: option.dot, flexShrink: 0, marginTop: 5 }} />
                      <span>
                        <span style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem' }}>{option.label}</span>
                        <span style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)' }}>{option.description}</span>
                      </span>
                    </li>
                  )}
                />
              </Box>

              {/* Conversation State Filter */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Conversation State
                </Typography>
                <Autocomplete
                  size="small"
                  options={CATEGORY_FILTER_OPTIONS}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  value={CATEGORY_FILTER_OPTIONS.find((o) => o.value === filters.conversationState) ?? null}
                  onChange={(_, selected) => {
                    onFilterChange('conversationState', selected?.value ?? null);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Filter by state" size="small" />
                  )}
                  renderOption={({ key, ...props }, option) => (
                    <li key={key} {...props} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px' }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: option.color, flexShrink: 0 }} />
                      <span style={{ fontSize: '0.875rem' }}>{option.label}</span>
                    </li>
                  )}
                />
              </Box>

              {/* Tags Filter */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Tags
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo={isAdmin}
                  size="small"
                  options={allTags.map(t => t.title)}
                  value={filters.tags || []}
                  onChange={async (_, newValue) => {
                    // If admin added a new tag that doesn't exist, create it
                    if (isAdmin) {
                      const newTags = newValue.filter(tag => typeof tag === 'string' && !allTags.find(t => t.title === tag));
                      for (const newTag of newTags) {
                        if (typeof newTag === 'string') {
                          try {
                            await createTagMutation.mutateAsync(newTag);
                          } catch (error) {
                            // Tag might already exist, ignore
                          }
                        }
                      }
                    }
                    onFilterChange('tags', newValue.filter((v): v is string => typeof v === 'string'));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder={isAdmin ? "Select or create tags" : "Select tags"} size="small" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        label={option}
                        size="small"
                      />
                    ))
                  }
                />
              </Box>

              {/* Last Sender Filter */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Last Sender
                </Typography>
                <EntityMultiSelect
                  options={[
                    { uuid: 'pipeline', name: 'Pipeline' },
                    { uuid: 'system', name: 'System' },
                    ...owners,
                  ]}
                  valueUuids={filters.lastSenders}
                  onChange={(uuids) => onFilterChange('lastSenders', uuids)}
                  placeholder="Select last sender"
                  size="small"
                />
              </Box>

            </Stack>
          </Collapse>
        </LocalizationProvider>
      </Box>

      {/* Message List */}
      <Box ref={containerRef} sx={{ flexGrow: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load messages
          </Alert>
        ) : filteredChats.length === 0 ? (
          <EmptyState
            title="No messages found"
            description="Try adjusting your filters or check back later"
            icon={<SearchIcon sx={{ fontSize: 48 }} />}
          />
        ) : (
          <List sx={{ p: 0 }}>
            {filteredChats.map((chat, index) => {
                const lastMessage = chat.messages?.items?.[0];
                const lastMessagePrefix = (() => {
                  if (!lastMessage) return '';
                  if (lastMessage.sender === 'pipeline') return 'Pipeline: ';
                  if (lastMessage.sender === 'ai') return 'AI: ';
                  if (lastMessage.sender === 'system') return 'System: ';
                  return lastMessage.is_sender ? 'You: ' : '';
                })();
                const prospectName =
                  chat.prospect?.full_name ||
                  (chat.prospect?.first_name && chat.prospect?.last_name
                    ? `${chat.prospect.first_name} ${chat.prospect.last_name}`
                    : chat.prospect?.first_name ||
                      chat.prospect?.last_name ||
                      'Unknown');
                return (
                  <React.Fragment key={chat.key}>
                    <ListItem
                      sx={{
                        bgcolor: selectedChat?.key === chat.key ? 'primary.50' : 'transparent',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        cursor: 'pointer',
                        borderRadius: 1,
                        mb: 1,
                        transition: 'background-color 0.2s ease',
                      }}
                      onClick={() => onChatSelect(chat)}
                    >
                      <ListItemAvatar>
                        <Badge
                          color="primary"
                          variant="dot"
                          invisible={chat.seen_last_message}
                        >
                          <Avatar 
                            src={chat.prospect.profile_image || undefined}
                            sx={{ bgcolor: 'primary.light', color: 'primary.main' }}
                          >
                            {prospectName.charAt(0).toUpperCase()}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      
                      <ListItemText
                        disableTypography
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: !chat.seen_last_message ? 600 : 400,
                                flex: 1,
                              }}
                            >
                              {prospectName}
                            </Typography>
                            {chat.conversation_state && (
                              <Chip
                                label={getCategoryLabel(chat.conversation_state)}
                                size="small"
                                sx={{
                                  bgcolor: getCategoryColor(chat.conversation_state),
                                  color: '#fff',
                                  fontSize: '0.6rem',
                                  height: '18px',
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            )}
                            {chat.failed_scheduled_count != null && chat.failed_scheduled_count > 0 && (
                              <Tooltip title={`${chat.failed_scheduled_count} failed message(s)`}>
                                <EventBusyIcon
                                  sx={{ fontSize: 16, color: 'error.main' }}
                                />
                              </Tooltip>
                            )}
                            {chat.scheduled_count != null && chat.scheduled_count > 0 && (
                              <Tooltip title={`${chat.scheduled_count} scheduled message(s)`}>
                                <CalendarIcon
                                  sx={{ fontSize: 16, color: 'primary.main' }}
                                />
                              </Tooltip>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {new Date(chat.last_received_date || chat.last_send_date || chat.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: !chat.seen_last_message ? 600 : 400,
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {lastMessage ? (
                                <>
                                  {lastMessagePrefix}
                                  {lastMessage.text}
                                </>
                              ) : (
                                'No messages yet'
                              )}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                              <ProjectStreamTag
                                project={chat.project}
                                stream={chat.stream}
                                compact={true}
                              />
                              <MessageCategoriesTags
                                categories={chat.message_categories}
                                compact={true}
                              />
                              <TagsDisplay
                                tags={chat.tags || []}
                                compact={true}
                              />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < filteredChats.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            {/* Inline spinner for next-page loading */}
            {isFetchingNextPage && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, px: 2 }}>
                <CircularProgress size={80} thickness={4} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading more messages...
                </Typography>
              </Box>
            )}
          </List>
        )}
        {/* Sentinel for infinite scroll */}
        <Box ref={loadMoreRef} sx={{ height: '4px' }} />
      </Box>
    </Box>
  );
};
